<template>
  <teleport to="body">
    <div class="container-fluid">
      <confirm-dialog ref="confirmDialog"></confirm-dialog>

      <notifications
        width="500"
        group="bb-confirm"
        :speed="100"
        :duration="1200"
        position="top right"
        classes="vue-notification"
      />

      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-hdg mb-3 text-center">House Rules</div>

            <div
              v-if="errorMessage"
              class="row mt-3 mb-3"
            >
              <div class="form-group">
                <div class="row">
                  <div class="col text-end me-3">
                    <div
                      class="bb-error-wrap"
                      style="white-space: pre-line"
                    >
                      <div class="row mb-3">*** {{ errorMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <div class="bb-popup-table-controls mb-3">
              <div class="col-4 bb-house-rules-search-shell">
                <div class="form-floating">
                  <input
                    id="bb-house-rules-search-input"
                    v-model.trim="searchTerm"
                    type="text"
                    class="form-control bb-house-rules-search-input"
                    placeholder="Search..."
                  />
                  <label
                    class="bb-house-rules-field-label"
                    for="bb-house-rules-search-input"
                    >Search Subject</label
                  >
                </div>
              </div>

              <!-- --------------------- -->
              <!--  category (selection) -->
              <!-- --------------------- -->
              <div class="col-2 form-group">
                <div class="form-floating">
                  <v-select
                    :options="displayCategories"
                    class="form-control"
                    :modelValue="category"
                    @update:modelValue="onCategoryChange($event)"
                    id="category"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                      />
                    </template>
                  </v-select>
                  <label
                    class="bb-float-label optional"
                    for="category"
                    >Category</label
                  >
                </div>
              </div>

              <div class="bb-popup-table-actions">
                <button
                  type="button"
                  class="btn btn-primary btn-lg"
                  @click="openAddDialog"
                >
                  Add
                </button>
                <button
                  type="button"
                  class="btn btn-warning btn-lg"
                  @click="handleClose"
                >
                  Cancel
                </button>
              </div>
            </div>

            <!-- House Rules Table -->
            <div class="bb-house-rules-table-shell">
              <table
                class="table table-dark table-striped table-hover align-middle mb-0 bb-house-rules-table"
              >
                <!-- Table Header -->
                <thead>
                  <tr>
                    <th scope="col">Subject</th>
                    <th scope="col">Category</th>
                    <th scope="col">Added By</th>
                    <th scope="col">Updated</th>
                    <th
                      scope="col"
                      class="text-center"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <!-- No House Rules Message -->
                  <tr v-if="!searchHouseRules.length && !isLoading">
                    <td
                      colspan="5"
                      class="text-center py-4"
                    >
                      {{ emptyMessage }}
                    </td>
                  </tr>

                  <!-- House Rule Rows -->
                  <tr
                    v-for="houseRule in searchHouseRules"
                    :key="houseRule.id"
                  >
                    <!-- Subject -->
                    <td class="bb-house-rule-subject">
                      {{ houseRule.subject }}
                    </td>

                    <!-- Category -->
                    <td>{{ houseRule.category || '-' }}</td>

                    <!-- Added By -->
                    <td>{{ houseRule.added_by || '-' }}</td>

                    <!-- Updated Date -->
                    <td>{{ formatDate(houseRule.updated_at) }}</td>

                    <!-- Actions -->
                    <td class="text-center">
                      <div class="bb-house-rules-row-actions">
                        <!-- View Action -->
                        <button
                          type="button"
                          class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
                          @click="openDocument(houseRule)"
                        >
                          <ToolTip
                            text="View House Rule"
                            position="left"
                          >
                            <img
                              :src="viewImg"
                              height="40"
                              width="40"
                              alt="view image"
                            />
                          </ToolTip>
                        </button>

                        <!-- Edit Action -->
                        <button
                          type="button"
                          class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
                          @click="openEditDialog(houseRule)"
                        >
                          <ToolTip
                            text="Edit House Rule"
                            position="left"
                          >
                            <img
                              :src="editImg"
                              height="40"
                              width="40"
                              alt="edit image"
                            />
                          </ToolTip>
                        </button>

                        <!-- Delete Action -->
                        <button
                          type="button"
                          class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center"
                          @click="deleteHouseRule(houseRule)"
                        >
                          <ToolTip
                            text="Delete House Rule"
                            position="left"
                          >
                            <img
                              :src="deleteImg"
                              height="48"
                              width="48"
                              alt="delete image"
                            />
                          </ToolTip>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isDialogOpen"
        class="bb-popup-bg d-flex justify-content-center align-items-center bb-house-rules-dialog-bg"
      >
        <div class="card shadow bb-card rounded bb-house-rules-dialog-card">
          <div class="card-body p-4">
            <div class="bb-sub-hdg text-center mb-2">{{ dialogTitle }}</div>

            <div
              v-if="dialogErrorMessage"
              class="row mt-2 mb-3"
            >
              <div class="form-group">
                <div class="row">
                  <div class="col text-end me-3">
                    <div
                      class="bb-error-wrap"
                      style="white-space: pre-line"
                    >
                      <div class="row mb-3">*** {{ dialogErrorMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class=" row mb-3">
              <!-- subject -->
              <div class="col-8 form-group">
                <div class="form-floating">
                  <input
                    id="house-rule-subject"
                    v-model.trim="form.subject"
                    type="text"
                    class="form-control bb-house-rules-dialog-field"
                    maxlength="200"
                    placeholder="Subject..."
                  />
                  <label
                    class="bb-float-label bb-house-rules-field-label"
                    for="house-rule-subject"
                    >Subject</label
                  >
                </div>
                <div
                  v-if="subjectValidationMessage"
                  class="row mt-2"
                >
                  <div class="form-group">
                    <div class="row">
                      <div class="col text-end me-3">
                        <div
                          class="bb-error-wrap"
                          style="white-space: pre-line"
                        >
                          <div class="row mb-0">
                            *** {{ subjectValidationMessage }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- category @update:modelValue="onCategoryChange($event)"
-->
              <div class="col-3 form-group">
                <div class="form-floating">
                  <v-select
                    :options="categories"
                    class="form-control"
                    :modelValue="form.category"
                    @update:modelValue="onDialogCategoryChange($event)"
                    id="category"
                  >
                  </v-select>
                  <label
                    class="bb-float-label optional"
                    for="category"
                    >Category</label
                  >
                </div>
              </div>


            </div>

            <div class="mb-3 form-group bb-house-rules-document-picker-wrap">
              <input
                id="house-rule-document"
                ref="referenceDocumentInput"
                type="file"
                class="bb-house-rules-document-input"
                accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.gif,.webp,.bmp"
                placeholder="Document or Image"
                @change="handleReferenceDocumentChange"
              />
              <div
                class="row g-3 align-items-stretch bb-house-rules-document-row"
              >
                <div class="col">
                  <div class="form-floating">
                    <input
                      id="house-rule-document-display"
                      type="text"
                      class="form-control bb-house-rules-dialog-field"
                      :value="displayDocumentName"
                      disabled
                      readonly
                    />
                    <label
                      class="bb-float-label bb-house-rules-field-label"
                      for="house-rule-document-display"
                      >Document / Image</label
                    >
                  </div>
                </div>
                <div class="col-auto d-flex align-items-stretch">
                  <button
                    type="button"
                    class="btn btn-primary btn-lg bb-house-rules-file-picker-button"
                    @click="openReferenceDocumentPicker"
                  >
                    Choose File
                  </button>
                </div>
              </div>
              <div
                v-if="documentValidationMessage"
                class="row mt-2"
              >
                <div class="form-group">
                  <div class="row">
                    <div class="col text-end me-3">
                      <div
                        class="bb-error-wrap"
                        style="white-space: pre-line"
                      >
                        <div class="row mb-0">
                          *** {{ documentValidationMessage }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              class="row g-3 mb-3 align-items-end bb-house-rules-dialog-footer-row"
            >
              <div class="col-sm-5 bb-house-rules-added-by-col">
                <div class="form-group">
                  <div class="form-floating">
                    <input
                      id="house-rule-added-by"
                      :value="displayAddedBy"
                      type="text"
                      class="form-control bb-house-rules-dialog-field"
                      disabled
                      readonly
                      placeholder="Added By"
                    />
                    <label
                      class="bb-float-label bb-house-rules-field-label optional"
                      for="house-rule-added-by"
                      >Added By</label
                    >
                  </div>
                </div>
              </div>
              <div
                class="col-auto ms-auto d-flex justify-content-end bb-house-rules-dialog-actions-col"
              >
                <div class="bb-house-rules-dialog-actions">
                  <button
                    v-if="isEditing && form.referenceDocumentUrl"
                    type="button"
                    class="btn btn-primary btn-lg"
                    :disabled="isSaving"
                    @click="openDocument(form)"
                  >
                    Open Document
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary btn-lg"
                    :disabled="!canSubmit"
                    @click="submitDialog"
                  >
                    {{ isSaving ? 'Saving...' : 'Ok' }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-warning btn-lg"
                    :disabled="isSaving"
                    @click="closeDialog"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'

import ConfirmDialog from '../components/ConfirmDialog.vue'
import ToolTip from '../components/ToolTip.vue'

import capitaliseMultipleWords from '../helpers/capitaliseMultipleWords'
import showNotification from '../helpers/showNotification'

import deleteImg from '../assets/images/bbDelete48.png'
import editImg from '../assets/images/bbEdit.png'
import viewImg from '../assets/images/bbView.png'

const HOUSE_RULES_ENDPOINT = 'api/v1/campaigns/house-rules/'
const HOUSE_RULE_LOOKUPS_ENDPOINT = `${HOUSE_RULES_ENDPOINT}lookups/`

function emptyForm() {
  return {
    id: '',
    subject: '',
    category: '',
    added_by: '',
    referenceDocumentFile: null,
    referenceDocumentName: '',
    referenceDocumentUrl: '',
  }
}

function normaliseHouseRuleSubject(subject) {
  return capitaliseMultipleWords(String(subject || '').trim())
}

export default {
  name: 'HouseRulesView',
  components: {
    ConfirmDialog,
    ToolTip,
  },
  data() {
    return {
      editImg,
      deleteImg,
      viewImg,
      searchTerm: '',
      category: 'All',
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      dialogErrorMessage: '',
      houseRules: [],
      displayHouseRules: [],
      categories: [],
      displayCategories: [],
      isDialogOpen: false,
      dialogMode: 'add',
      form: emptyForm(),
      originalFormSnapshot: null,
    }
  },
  computed: {
    ...mapGetters('auth', {
      getUserProfile: 'getUserProfile',
    }),
    ...mapGetters('combat', {
      getCampaign: 'getCampaign',
    }),
    searchHouseRules() {
      const term = this.searchTerm.trim().toLowerCase()

      if (!term) {
        return this.displayHouseRules
      }

      return this.displayHouseRules.filter((houseRule) =>
        String(houseRule.subject || '')
          .toLowerCase()
          .includes(term),
      )
    },
    emptyMessage() {
      if (this.searchTerm.trim()) {
        return `No house rules matched "${this.searchTerm.trim()}".`
      }

      return 'No house rules found.'
    },
    isEditing() {
      return this.dialogMode === 'edit'
    },
    dialogTitle() {
      return this.isEditing ? 'Edit House Rule' : 'Add House Rule'
    },
    currentUserName() {
      return this.getUserProfile?.user_name || ''
    },
    displayAddedBy() {
      return this.form.added_by || this.currentUserName || '-'
    },
    selectedDocumentName() {
      return this.form.referenceDocumentFile?.name || ''
    },
    displayDocumentName() {
      return (
        this.selectedDocumentName ||
        (this.isEditing ? this.form.referenceDocumentName || '' : '') ||
        'Select file...'
      )
    },
    isDuplicateSubject() {
      const subject = this.form.subject.trim().toLowerCase()
      const originalSubject = String(this.originalFormSnapshot?.subject || '')
        .trim()
        .toLowerCase()

      if (!subject) {
        return false
      }

      if (this.isEditing && subject === originalSubject) {
        return false
      }

      return this.houseRules.some((houseRule) => {
        if (
          this.isEditing &&
          String(houseRule.id || '') === String(this.form.id || '')
        ) {
          return false
        }

        return (
          String(houseRule.subject || '')
            .trim()
            .toLowerCase() === subject
        )
      })
    },
    subjectValidationMessage() {
      if (this.isDuplicateSubject) {
        return 'Subject must be unique.'
      }

      return ''
    },
    documentValidationMessage() {
      if (
        !this.isSupportedReferenceDocumentFile(this.form.referenceDocumentFile)
      ) {
        return this.form.referenceDocumentFile
          ? 'Reference document must be a PDF or image file.'
          : ''
      }

      return ''
    },
    hasFormChanges() {
      if (!this.originalFormSnapshot) {
        return false
      }

      const subjectChanged =
        this.form.subject.trim() !== this.originalFormSnapshot.subject
      const categoryChanged =
        String(this.form.category || '') !==
        String(this.originalFormSnapshot.category || '')
      const documentChanged = !!this.form.referenceDocumentFile

      return subjectChanged || categoryChanged || documentChanged
    },
    canSubmit() {
      if (this.isSaving) {
        return false
      }

      if (!this.form.subject.trim() || this.isDuplicateSubject) {
        return false
      }

      if (
        !this.isSupportedReferenceDocumentFile(this.form.referenceDocumentFile)
      ) {
        return false
      }

      if (this.isEditing) {
        return this.hasFormChanges
      }

      return !!this.form.referenceDocumentFile
    },
  },
  mounted() {
    this.loadHouseRules()
    this.loadCategories()
  },
  watch: {
    'form.subject'(nextValue) {
      const normalisedValue = capitaliseMultipleWords(nextValue)

      if (nextValue !== normalisedValue) {
        this.form.subject = normalisedValue
      }
    },
  },
  methods: {
    onCategoryChange(value) {
      this.category = value
      this.loadHouseRulesByCategory(value)
    },
    resetTableCategoryFilter() {
      this.category = 'All'
      this.loadHouseRulesByCategory('All')
    },
    onDialogCategoryChange(value) {
      this.form.category = value || ''
    },
    loadHouseRulesByCategory(category) {
      const displayRules =
        this.category === 'All'
          ? this.houseRules
          : this.houseRules.filter((i) => i.category === category)

      const sortedRules = displayRules.sort((a, b) => {
        return a.subject.localeCompare(b.subject)
      })
      this.displayHouseRules = sortedRules
      this.searchHouseRules
    },
    buildAbsoluteUrl(path) {
      if (!path) {
        return ''
      }

      if (/^https?:\/\//i.test(path)) {
        return path
      }

      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      const baseUrl = import.meta.env.VITE_API_URL || window.location.origin

      try {
        return new URL(normalizedPath, baseUrl).toString()
      } catch {
        return normalizedPath
      }
    },
    isSupportedReferenceDocumentFile(file) {
      if (!file) {
        return true
      }

      const fileName = String(file.name || '').toLowerCase()
      const mimeType = String(file.type || '').toLowerCase()

      const allowedImageExtensions = [
        '.png',
        '.jpg',
        '.jpeg',
        '.gif',
        '.webp',
        '.bmp',
      ]

      return (
        mimeType === 'application/pdf' ||
        mimeType.startsWith('image/') ||
        fileName.endsWith('.pdf') ||
        allowedImageExtensions.some((extension) => fileName.endsWith(extension))
      )
    },
    normaliseResponseRows(payload) {
      const rows = Array.isArray(payload) ? payload : payload?.results || []

      return rows.map((houseRule) => ({
        ...houseRule,
        subject: normaliseHouseRuleSubject(houseRule?.subject),
      }))
    },
    async loadCategories() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(HOUSE_RULE_LOOKUPS_ENDPOINT, {
          params: {
            limit: 200,
          },
        })
        this.categories = response.data.category || []
        this.displayCategories = this.categories.slice()
        this.displayCategories.push('All')
      } catch (error) {
        this.categories = []
         this.displayCategories = []
        this.errorMessage =
          error?.response?.data?.detail || 'Unable to load house rule lookups.'
      } finally {
        this.isLoading = false
      }
    },
    async loadHouseRules() {
      this.isLoading = true
      this.errorMessage = ''

      try {
        const response = await axios.get(HOUSE_RULES_ENDPOINT, {
          params: {
            limit: 200,
          },
        })

        // save actual house rules before any filtering
        this.houseRules = this.normaliseResponseRows(response.data)

        // set displayHouseRules to the full list initially
        this.displayHouseRules = this.houseRules
      } catch (error) {
        this.houseRules = []
        this.errorMessage =
          error?.response?.data?.detail || 'Unable to load house rules.'
      } finally {
        this.isLoading = false
      }
    },
    getDocumentUrl(houseRule) {
      return this.buildAbsoluteUrl(
        houseRule?.reference_document_url ||
          houseRule?.referenceDocumentUrl ||
          houseRule?.reference_document ||
          houseRule?.referenceDocument ||
          '',
      )
    },
    getDocumentName(houseRule) {
      if (houseRule?.reference_document_name) {
        return houseRule.reference_document_name
      }

      const documentUrl = this.getDocumentUrl(houseRule)
      if (!documentUrl) {
        return ''
      }

      return documentUrl.split('/').pop() || 'Open document'
    },
    formatDate(value) {
      if (!value) {
        return '-'
      }

      const nextDate = new Date(value)
      if (Number.isNaN(nextDate.getTime())) {
        return '-'
      }

      return nextDate.toLocaleString()
    },
    openDocument(houseRule) {
      const documentUrl = this.getDocumentUrl(houseRule)
      if (!documentUrl) {
        return
      }

      window.open(documentUrl, '_blank', 'noopener')
    },
    openAddDialog() {
      this.dialogMode = 'add'
      this.dialogErrorMessage = ''
      this.form = {
        ...emptyForm(),
        category: 'Other',
        added_by: this.currentUserName,
      }
      this.originalFormSnapshot = {
        subject: '',
        category: this.form.category || '',
      }
      this.isDialogOpen = true
    },
    openEditDialog(houseRule) {
      this.dialogMode = 'edit'
      this.dialogErrorMessage = ''
      this.form = {
        id: houseRule.id,
        subject: normaliseHouseRuleSubject(houseRule.subject),
        category: houseRule.category || '',
        added_by: houseRule.added_by || '',
        referenceDocumentFile: null,
        referenceDocumentName: houseRule.reference_document_name || '',
        referenceDocumentUrl: this.getDocumentUrl(houseRule),
      }
      this.originalFormSnapshot = {
        subject: (houseRule.subject || '').trim(),
        category: houseRule.category || '',
      }
      this.isDialogOpen = true
    },
    closeDialog() {
      this.isDialogOpen = false
      this.dialogErrorMessage = ''
      this.form = emptyForm()
      this.originalFormSnapshot = null

      if (this.$refs.referenceDocumentInput) {
        this.$refs.referenceDocumentInput.value = ''
      }
    },
    handleReferenceDocumentChange(event) {
      const [file] = event?.target?.files || []
      this.form.referenceDocumentFile = file || null

      if (file && !this.isSupportedReferenceDocumentFile(file)) {
        this.dialogErrorMessage =
          'Reference document must be a PDF or image file.'
      } else {
        this.dialogErrorMessage = ''
      }
    },
    openReferenceDocumentPicker() {
      this.$refs.referenceDocumentInput?.click()
    },
    buildFormData() {
      const formData = new FormData()
      formData.append('subject', normaliseHouseRuleSubject(this.form.subject))
      formData.append('category', this.form.category || '')

      if (this.form.referenceDocumentFile) {
        formData.append('reference_document', this.form.referenceDocumentFile)
      }

      return formData
    },
    getSaveErrorMessage(error, fallbackMessage) {
      const data = error?.response?.data || {}

      if (typeof data?.detail === 'string') {
        return data.detail
      }

      if (Array.isArray(data?.subject) && data.subject.length > 0) {
        return data.subject[0]
      }

      if (
        Array.isArray(data?.reference_document) &&
        data.reference_document.length > 0
      ) {
        return data.reference_document[0]
      }

      return fallbackMessage
    },
    async submitDialog() {
      if (!this.canSubmit) {
        return
      }

      this.isSaving = true
      this.dialogErrorMessage = ''

      try {
        const payload = this.buildFormData()

        if (this.isEditing) {
          await axios.patch(`${HOUSE_RULES_ENDPOINT}${this.form.id}/`, payload)
          showNotification(`House rule ${this.form.subject.trim()} updated.`)
        } else {
          await axios.post(HOUSE_RULES_ENDPOINT, payload)
          showNotification(`House rule ${this.form.subject.trim()} added.`)
        }

        await this.loadHouseRules()
        this.resetTableCategoryFilter()
        this.closeDialog()
      } catch (error) {
        this.dialogErrorMessage = this.getSaveErrorMessage(
          error,
          this.isEditing
            ? 'Unable to update house rule.'
            : 'Unable to add house rule.',
        )
      } finally {
        this.isSaving = false
      }
    },
    async deleteHouseRule(houseRule) {
      if (!houseRule?.id) {
        return
      }

      const ok = await this.$refs.confirmDialog?.show?.({
        title: 'Delete House Rule',
        message: 'Confirm delete',
        subject: houseRule.subject || 'House Rule',
        okButton: 'Delete',
        cancelButton: 'Cancel',
      })

      if (!ok) {
        return
      }

      this.errorMessage = ''

      try {
        await axios.delete(`${HOUSE_RULES_ENDPOINT}${houseRule.id}/`)
        this.houseRules = this.houseRules.filter(
          (row) => row.id !== houseRule.id,
        )
        this.displayHouseRules = this.displayHouseRules.filter(
          (row) => row.id !== houseRule.id,
        )
        showNotification(`House rule ${houseRule.subject} deleted.`)
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.detail || 'Unable to delete house rule.'
      }
    },
    handleClose() {
      if (this.$route.query?.popup === 'house-rules') {
        const query = { ...this.$route.query }
        delete query.popup

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      const arenaSlug =
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.getCampaign?.slug ||
        ''

      if (arenaSlug) {
        this.$router.replace({
          name: 'manageCombat',
          params: { slug: arenaSlug },
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-table-card {
  width: min(90rem, 98vw);
  height: min(90vh, 76rem);
  max-height: min(90vh, 76rem);
  position: relative;
  overflow: hidden;
  margin-left: auto;
  margin-right: 1.5rem;
}

.bb-popup-table-body {
  padding: 1.5rem 2.5rem 1.5rem !important;
}

.bb-popup-table-loader {
  margin-bottom: 1rem;
}

.bb-popup-table-controls {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.bb-house-rules-search-shell {
  width: min(42rem, 100%);
}

.bb-house-rules-search-input {
  text-transform: none;
  font-size: 1.4rem;
  min-height: 4rem;
  padding-top: 1.7rem;
}

.bb-house-rules-search-input::placeholder {
  color: white !important;
  -webkit-text-fill-color: white !important;
  opacity: 1;
}

.bb-house-rules-field-label {
  font-size: 1.15rem;
  padding-top: 0.55rem;
}

.bb-popup-table-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.bb-house-rules-table-shell {
  height: calc(90vh - 17rem);
  max-height: calc(90vh - 17rem);
  overflow-x: auto;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.5rem;
}

.bb-house-rules-table {
  margin-bottom: 0;
  font-size: 1.3rem;
}

.bb-house-rules-table tbody td {
  padding-top: 1rem;
  padding-bottom: 1rem;
  min-height: 5rem;
  vertical-align: middle;
}

.bb-house-rules-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: rgba(19, 25, 38, 0.98);
  font-size: 1.15rem;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.bb-house-rule-subject {
  min-width: 16rem;
}

.bb-house-rules-row-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.bb-house-rules-link {
  text-decoration: none;
}

.bb-house-rules-dialog-bg {
  z-index: 30;
}

.bb-house-rules-dialog-card {
  width: min(68rem, calc(100vw - 1rem));
}

.bb-house-rules-dialog-card :deep(.form-text),
.bb-house-rules-dialog-card :deep(.btn) {
  font-size: 1.3rem;
}

.bb-house-rules-dialog-card :deep(.card-body) {
  padding: 2rem 3rem 3rem !important;
}

.bb-house-rules-dialog-field {
  min-height: 4.75rem !important;
  font-size: 1.5rem !important;
  padding-top: 1.9rem !important;
  padding-bottom: 0.9rem !important;
}

.bb-house-rules-document-picker-wrap {
  position: relative;
}

.bb-house-rules-document-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.bb-house-rules-file-picker-button {
  flex: 0 0 auto;
  min-height: 4.75rem;
  align-self: stretch;
}

.bb-house-rules-added-by-col {
  width: 36%;
  max-width: 36%;
}

.bb-house-rules-dialog-card :deep(.bb-float-label),
.bb-house-rules-dialog-card .bb-float-label,
.bb-house-rules-dialog-card .bb-house-rules-field-label {
  font-size: 1.15rem !important;
}

.bb-house-rules-dialog-card
  :deep(.form-floating > .bb-house-rules-dialog-field ~ label) {
  padding-top: 0.75rem;
}

.bb-house-rules-dialog-card :deep(.btn-primary),
.bb-house-rules-dialog-card :deep(.btn-warning) {
  min-width: 9rem;
  min-height: 3.75rem;
}

:deep(.bb-hdg) {
  font-size: 2.4rem;
}

:deep(.bb-sub-hdg) {
  font-size: 1.9rem;
}

.bb-house-rules-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: auto;
}

@media (max-width: 768px) {
  .bb-popup-table-card {
    width: calc(100vw - 1rem);
    max-height: calc(100vh - 1rem);
    height: calc(100vh - 1rem);
  }

  .bb-popup-table-body {
    padding: 1.5rem 1.5rem 1.25rem !important;
  }

  .bb-popup-table-controls {
    flex-direction: column;
  }

  .bb-house-rules-search-shell {
    width: 100%;
  }

  .bb-popup-table-actions {
    width: 100%;
    justify-content: stretch;
  }

  .bb-popup-table-actions .btn {
    flex: 1 1 0;
  }

  .bb-house-rules-added-by-col {
    width: 100%;
    max-width: 100%;
  }

  .bb-house-rules-dialog-actions {
    flex-direction: column-reverse;
    width: 100%;
  }

  .bb-house-rules-dialog-actions .btn {
    width: 100%;
  }

  .bb-house-rules-dialog-card {
    width: calc(100vw - 1rem);
  }
}
</style>
